import React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export function Card({ className, children, hover = false, ...props }) {
  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg",
        hover && "transition-shadow duration-200 hover:shadow-sm hover:-translate-y-px",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function Button({ variant = "primary", size = "md", className, children, ...props }) {
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-brand-800 active:scale-[0.98]",
    outline: "border border-border bg-card text-foreground hover:bg-muted",
    ghost: "text-foreground hover:bg-muted",
    danger: "bg-destructive text-white hover:bg-red-600 active:scale-[0.98]",
    subtle: "bg-accent text-accent-foreground hover:bg-brand-100",
  };
  const sizes = { sm: "h-8 px-3 text-xs", md: "h-10 px-4 text-sm", lg: "h-11 px-6 text-sm" };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors duration-200 disabled:opacity-50 disabled:pointer-events-none",
        variants[variant], sizes[size], className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

const badgeTones = {
  High: "bg-red-100 text-red-800", Rising: "bg-amber-100 text-amber-800", Low: "bg-indigo-100 text-indigo-800",
  Urgent: "bg-red-100 text-red-800", Medium: "bg-amber-100 text-amber-800", High_p: "bg-orange-100 text-orange-800",
  Open: "bg-blue-100 text-blue-800", "In Progress": "bg-amber-100 text-amber-800", Completed: "bg-indigo-100 text-indigo-800",
  Active: "bg-indigo-100 text-indigo-800", Paid: "bg-indigo-100 text-indigo-800", Denied: "bg-red-100 text-red-800",
  Pending: "bg-slate-100 text-foreground", Approved: "bg-indigo-100 text-indigo-800",
  "Above Target": "bg-indigo-100 text-indigo-800", "Below Target": "bg-red-100 text-red-800",
  "In-Network": "bg-indigo-100 text-indigo-800", "Out-of-Network": "bg-slate-100 text-foreground",
  default: "bg-slate-100 text-foreground",
};

export function Badge({ tone, children, className }) {
  const cls = badgeTones[tone] || badgeTones[children] || badgeTones.default;
  return (
    <span className={cn("inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold", cls, className)}>
      {children}
    </span>
  );
}

export function StatCard({ label, value, sub, icon: Icon, tone = "brand", testId }) {
  const tones = {
    brand: "text-brand-700 bg-brand-50", red: "text-red-700 bg-red-50",
    amber: "text-amber-700 bg-amber-50", blue: "text-blue-700 bg-blue-50",
  };
  return (
    <Card className="p-5" hover data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
          <p className="mt-2 text-3xl font-bold font-display tracking-tight text-foreground">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        {Icon && (
          <div className={cn("h-10 w-10 rounded-md flex items-center justify-center", tones[tone])}>
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function Input({ className, ...props }) {
  return (
    <input
      className={cn(
        "h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow",
        className
      )}
      {...props}
    />
  );
}

export function Select({ className, children, ...props }) {
  return (
    <select
      className={cn(
        "h-10 rounded-md border border-input bg-card px-3 text-sm text-foreground",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
        className
      )}
      {...props}
    >
      {children}
    </select>
  );
}

export function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 backdrop-blur-sm p-4 sm:p-8">
      <div
        className={cn("bg-card rounded-lg border border-border shadow-xl w-full vc-fade-up", wide ? "max-w-3xl" : "max-w-lg")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-lg font-semibold font-display">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
